/**
 * In-memory store for application data.
 *
 * User data is never written to browser storage. Signed-in contexts are
 * responsible for loading and saving durable data through the server API.
 * Values kept here are discarded on reload and when the user signs out.
 */

const ANON_SESSION_TTL_MS = 60 * 60 * 1000;

const memoryStore = new Map<string, string>();

let _isAuthenticated = false;
let lastActivityAt = 0;

type StorageAuthListener = (authenticated: boolean) => void;
const listeners = new Set<StorageAuthListener>();

type AnonExpiryListener = () => void;
const expiryListeners = new Set<AnonExpiryListener>();

export function subscribeAnonSessionExpiry(listener: AnonExpiryListener): () => void {
  expiryListeners.add(listener);
  return () => {
    expiryListeners.delete(listener);
  };
}

export function subscribeStorageAuthState(listener: StorageAuthListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function touchActivity() {
  if (_isAuthenticated) return;
  lastActivityAt = Date.now();
}

export function touchAnonSession(): void {
  touchActivity();
}

export function checkAnonSessionExpiry(): boolean {
  return clearAnonSessionIfExpired();
}

function clearAnonSessionIfExpired(): boolean {
  if (_isAuthenticated || lastActivityAt === 0) return false;
  if (Date.now() - lastActivityAt <= ANON_SESSION_TTL_MS) return false;
  memoryStore.clear();
  lastActivityAt = 0;
  expiryListeners.forEach((listener) => {
    try {
      listener();
    } catch (error) {
      console.error("Anonymous session expiry listener failed:", error);
    }
  });
  return true;
}

export function setStorageAuthState(authenticated: boolean) {
  if (_isAuthenticated === authenticated) return;

  if (_isAuthenticated && !authenticated) {
    memoryStore.clear();
    lastActivityAt = 0;
  }

  _isAuthenticated = authenticated;
  listeners.forEach((listener) => {
    try {
      listener(authenticated);
    } catch (error) {
      console.error("Storage auth listener failed:", error);
    }
  });
}

export function getStorageAuthState(): boolean {
  return _isAuthenticated;
}

export function appGetItem(key: string): string | null {
  if (typeof window === "undefined") return null;
  clearAnonSessionIfExpired();
  touchActivity();
  return memoryStore.get(key) ?? null;
}

export function appSetItem(key: string, value: string): boolean {
  if (typeof window === "undefined") return false;
  clearAnonSessionIfExpired();
  touchActivity();
  memoryStore.set(key, value);
  return true;
}

export function appRemoveItem(key: string): void {
  memoryStore.delete(key);
}
